import { isExtraDeckCard } from '../types/cards'
import {
  CARD_FOIL_PACK_WEIGHTS,
  asCardFoil,
  rollChaseFoil,
  rollFoilFrom,
  type CardFoilId,
} from '../types/foil'
import { PROMO_CARD_ID, PROMO_PACK_ID } from '../events/lastCall'
import {
  PACK_LOGIC_BY_ID,
  isPackId,
  type PackId,
  type PackLogicDef,
  type PackPool,
} from './ids'
import {
  PRINTED_RARITY_RANK,
  highestPrintedRarity,
  type PrintedRarity,
} from './printed'

/** Catalog fields needed to pick a pack pool. */
export type PackCardHint = {
  id: string | number
  type?: string
  frameType?: string
  typeline?: string[]
  humanReadableCardType?: string
  /** Album set prefix, when the catalog printing has one. */
  set?: string
  /** One row per legal expansion this card was printed in. */
  printings?: readonly { set: string; rarity: PrintedRarity }[]
}

export type PackRoll = {
  cardId: string
  foil: CardFoilId
}

export type PackPullWire = {
  cardId: string
  foil: CardFoilId
  isNew: boolean
}

/** Commons in a 2002 booster. The ninth card is Rare or better. */
const BOOSTER_COMMON_SLOTS = 8

/**
 * Ninth-card weights out of 24 packs. Secret is 1/24, Ultra 1/12, Super
 * 1/6. Rare takes the rest: 17/24 when the pool has a Secret, 3/4 when it
 * does not.
 */
const NINTH_ORDER = ['secret', 'ultra', 'super', 'rare'] as const

function ninthWeights(hasSecret: boolean): Record<(typeof NINTH_ORDER)[number], number> {
  return hasSecret
    ? { secret: 1, ultra: 2, super: 4, rare: 17 }
    : { secret: 0, ultra: 2, super: 4, rare: 18 }
}

function inPool(pool: PackPool, card: PackCardHint): boolean {
  const fusion = isExtraDeckCard({
    type: card.type ?? '',
    frameType: card.frameType ?? '',
    typeline: card.typeline ?? [],
    humanReadableCardType: card.humanReadableCardType ?? '',
  })
  const type = (card.type ?? '').toLowerCase()
  if (pool === 'all') return true
  if (pool === 'extra') return fusion
  if (pool === 'main-monster') {
    return type.includes('monster') && !fusion
  }
  return type.includes('spell') || type.includes('magic') || type.includes('trap')
}

/**
 * Card ids that can appear in this pack pool.
 */
export function idsInPackPool(
  pool: PackPool,
  catalog: readonly PackCardHint[],
): string[] {
  return catalog.filter((card) => inPool(pool, card)).map((card) =>
    String(card.id),
  )
}

function printedOf(card: PackCardHint): PrintedRarity {
  return highestPrintedRarity((card.printings ?? []).map((row) => row.rarity))
}

/** Printed rarity this pack uses for the card: the highest legal printing. */
export function packPrintedRarity(card: PackCardHint): PrintedRarity {
  return printedOf(card)
}

function admitsSet(card: PackCardHint, sets: readonly string[]): boolean {
  const have = new Set((card.printings ?? []).map((row) => row.set))
  if (card.set) have.add(card.set)
  return sets.some((set) => have.has(set))
}

function cardsForPack(
  def: PackLogicDef,
  catalog: readonly PackCardHint[],
): PackCardHint[] {
  return catalog.filter((card) => {
    if (!inPool(def.pool, card)) return false
    if (def.sets && def.sets.length > 0 && !admitsSet(card, def.sets)) return false
    if (def.set && !def.sets && !admitsSet(card, [def.set])) return false
    if (
      def.minRarity &&
      PRINTED_RARITY_RANK[printedOf(card)] < PRINTED_RARITY_RANK[def.minRarity]
    ) {
      return false
    }
    return true
  })
}

function pickCard(cards: readonly PackCardHint[], random: () => number): PackCardHint | null {
  if (cards.length === 0) return null
  const index = Math.floor(random() * cards.length)
  return cards[index] ?? cards[0] ?? null
}

function foilForPrinted(rarity: PrintedRarity, random: () => number): CardFoilId {
  if (rarity === 'ultra' || rarity === 'secret') return rollChaseFoil(random)
  if (rarity === 'rare' || rarity === 'super') return rarity
  return 'common'
}

function rollNinth(hasSecret: boolean, random: () => number): PrintedRarity {
  const weights = ninthWeights(hasSecret)
  let n = random() * 24
  for (const id of NINTH_ORDER) {
    if (weights[id] <= 0) continue
    n -= weights[id]
    if (n <= 0) return id
  }
  return 'rare'
}

function groupByRarity(
  cards: readonly PackCardHint[],
): Map<PrintedRarity, PackCardHint[]> {
  const groups = new Map<PrintedRarity, PackCardHint[]>()
  for (const card of cards) {
    const rarity = printedOf(card)
    const list = groups.get(rarity)
    if (list) list.push(card)
    else groups.set(rarity, [card])
  }
  return groups
}

function pickOfRarity(
  groups: Map<PrintedRarity, PackCardHint[]>,
  rolled: PrintedRarity,
  random: () => number,
): PackCardHint | null {
  const order: PrintedRarity[] = [rolled, 'rare', 'super', 'ultra', 'secret', 'common']
  for (const rarity of order) {
    const pool = groups.get(rarity)
    if (pool && pool.length > 0) return pickCard(pool, random)
  }
  return null
}

function rollBooster(
  cards: readonly PackCardHint[],
  random: () => number,
): PackRoll[] | null {
  const groups = groupByRarity(cards)
  const commons = groups.get('common') ?? []
  if (commons.length === 0) return null
  const pulls: PackRoll[] = []
  for (let i = 0; i < BOOSTER_COMMON_SLOTS; i++) {
    const card = pickCard(commons, random)
    if (!card) return null
    pulls.push({ cardId: String(card.id), foil: 'common' })
  }
  const ninth = pickOfRarity(
    groups,
    rollNinth((groups.get('secret')?.length ?? 0) > 0, random),
    random,
  )
  if (!ninth) return null
  pulls.push({
    cardId: String(ninth.id),
    foil: foilForPrinted(printedOf(ninth), random),
  })
  return pulls
}

function rollListed(
  cards: readonly PackCardHint[],
  count: number,
  foilOf: (card: PackCardHint) => CardFoilId,
  random: () => number,
): PackRoll[] | null {
  const pulls: PackRoll[] = []
  for (let i = 0; i < count; i++) {
    const card = pickCard(cards, random)
    if (!card) return null
    pulls.push({ cardId: String(card.id), foil: foilOf(card) })
  }
  return pulls
}

/**
 * Cards that can come out of this pack. Same filter the roller uses.
 */
export function cardsInPack(
  packId: PackId,
  catalog: readonly PackCardHint[],
): PackCardHint[] {
  if (!isPackId(packId)) return []
  if (packId === PROMO_PACK_ID) {
    return catalog.filter((card) => String(card.id) === PROMO_CARD_ID)
  }
  return cardsForPack(PACK_LOGIC_BY_ID[packId], catalog)
}

/**
 * Rolls one pack. Returns null when the id is unknown or the pool is empty.
 */
export function rollPack(
  packId: PackId,
  catalog: readonly PackCardHint[],
  random: () => number = Math.random,
): PackRoll[] | null {
  if (!isPackId(packId)) return null
  if (packId === PROMO_PACK_ID) {
    return [{ cardId: PROMO_CARD_ID, foil: rollChaseFoil(random) }]
  }
  const def = PACK_LOGIC_BY_ID[packId]
  const cards = cardsForPack(def, catalog)
  if (cards.length === 0) return null
  if (def.booster) return rollBooster(cards, random)
  if (def.minRarity) {
    return rollListed(
      cards,
      def.size,
      (card) => foilForPrinted(printedOf(card), random),
      random,
    )
  }
  if (def.kind === 'starter') {
    return rollListed(
      cards,
      def.size,
      (card) => foilForPrinted(printedOf(card), random),
      random,
    )
  }
  return rollListed(
    cards,
    def.size,
    () => rollFoilFrom(CARD_FOIL_PACK_WEIGHTS, random),
    random,
  )
}

/**
 * Marks first-time copies against a running owned-count map.
 */
export function rollsToPulls(
  rolls: PackRoll[],
  ownedBefore: Record<string, number>,
): PackPullWire[] {
  const seen = { ...ownedBefore }
  return rolls.map((roll) => {
    const was = seen[roll.cardId] ?? 0
    seen[roll.cardId] = was + 1
    return {
      cardId: roll.cardId,
      foil: asCardFoil(roll.foil),
      isNew: was === 0,
    }
  })
}
